import styled from 'styled-components'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, select } from '@/store'
import useMediaQuery from '@/hooks/use-media-query'
import hasVerified from '@/utils/has-verified'
import { ParagraphMedium, Label } from '@/components/text'
import { Button } from '@/components/button'
import { createModal, useModalize, AdditionalModalProps } from '@/components/modalize/mui'
import { Stack, Box } from '@/components/structure'
import { breakpoints, colors } from '@/styles'
import { FieldInput } from '@/components/form'
import { useToaster } from '@/models/toaster/hooks'

const StyledParagraph = styled(ParagraphMedium)`
  color: ${colors.grey48};
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  @media (max-width: ${breakpoints.phoneMax}) {
    button {
      width: 100%;
    }
  }
`

const StyledLabel = styled(Label)`
  margin-bottom: 12px;
`

type CompanyInfo = {
  business_licence_verified_state: string
}

type ProfileData = {
  business_licence: string
  company_info: CompanyInfo
}

type FormValues = {
  business_licence: string
}

const VerifyBusinessLicenceModal: React.FC<AdditionalModalProps> = ({
  ModalTitle,
  ModalFooter
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const toaster = useToaster()
  const dispatch = useDispatch<Dispatch>()
  const data: ProfileData = useSelector(select.profile.editLandlordProfile)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const padding = 24
  const isCompanyVerified = hasVerified(data.company_info?.business_licence_verified_state)
  const isCompanyVerificationPending =
    data.company_info?.business_licence_verified_state === 'pending'

  const getButtonText = () => {
    if (isCompanyVerified) {
      return isCompanyVerificationPending
        ? t('b.settings.view.verifying.text', 'Verifying...')
        : t('common.verified', 'Verified')
    } else {
      return t('b.settings.view.verify_business_licence.text', 'Verify Business License')
    }
  }

  const initialValues = { business_licence: data.business_licence || '' }
  const validationSchema = Yup.object().shape({
    business_licence: Yup.string().required()
  })
  const onSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const response = await dispatch.profile.save(values)
    setSubmitting(false)
    if (response.ok) {
      toaster.fetch()
      close()
    }
  }

  return (
    <>
      <ModalTitle>
        {t('l.profile.edit.business_license.subtitle', 'Verify Your Business Licence')}
      </ModalTitle>
      <Box ptop={padding} pbottom={padding} pleft={padding} pright={padding}>
        <Stack gutter="24px">
          <StyledParagraph>
            {t(
              'l.profile.edit.verify_business.description',
              'Become Verified by providing your Business License Number or your Brokerage License'
            )}
          </StyledParagraph>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, isSubmitting }) => (
              <Form>
                <StyledLabel>
                  {t('l.profile.edit.business_license_number.label', 'Business Licence Number')}
                </StyledLabel>
                <FieldInput
                  name="business_licence"
                  placeholder={t(
                    'l.profile.edit.identification_document.business_license',
                    'Business licence number'
                  )}
                  disabled={isCompanyVerified}
                />
                <ModalFooter>
                  <ButtonWrapper>
                    <Button
                      theme="primary"
                      type="submit"
                      size={displayMobile ? 'medium' : 'small'}
                      disabled={isSubmitting || isCompanyVerified}
                      onClick={handleSubmit}
                    >
                      {getButtonText()}
                    </Button>
                  </ButtonWrapper>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </Stack>
      </Box>
    </>
  )
}

export const showVerifyBusinessLicenceModal = createModal(VerifyBusinessLicenceModal, {
  width: '100%',
  maxWidth: '460px',
  sheet: true
})
